import * as z from 'zod';

import {
  AuthorizedContactId,
  BorrowerId,
  DelinquentInterestSettingId,
  EntityId,
  EscrowPaymentHistoryItemId,
  LoanId,
  PaymentConfigId,
  PaymentId,
  PayoffId,
  TransferId,
} from '../BrandedIds';
import { toJSInstantDate, zodJSInstantDate, zodJSMixedDate } from '../dates/BrandedDate';
import { zodBrandedUuid } from '../utils/Zod';
import {
  LoanStatusActive,
  LoanStatusForeclosure,
  LoanStatusPendingTransfer,
  LoanStatusPreActive,
  LoanStatusTransferred,
} from './LoanStatus';

export const PaymentStatus = z.enum(['PENDING', 'PROCESSED', 'FAILED', 'CANCELLED']);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const PaymentMethod = z.enum([
  'check',
  'wire',
  'phone',
  'creditCard',
  'debitCard',
  'dwolla',
  'reversal',
  'reserve',
  'reserveReversal',
  'balanceForward',
  'principalPrepayment',
  'authorizedACH',
  'repayACH',
  'balanceReallocation',
]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const NonAchPaymentMethods = z.enum([
  PaymentMethod.enum.check,
  PaymentMethod.enum.wire,
  PaymentMethod.enum.phone,
  PaymentMethod.enum.creditCard,
  PaymentMethod.enum.debitCard,
  PaymentMethod.enum.reserve,
  PaymentMethod.enum.principalPrepayment,
]);
export type NonAchPaymentMethods = z.infer<typeof NonAchPaymentMethods>;

export const AchPaymentMethods = z.enum([
  PaymentMethod.enum.dwolla,
  PaymentMethod.enum.authorizedACH,
  PaymentMethod.enum.repayACH,
]);
export type AchPaymentMethods = z.infer<typeof AchPaymentMethods>;

/**
 * Dwolla transfer
 */
const DwollaBorrowerTransferType = z.literal('balance');

// These are deprecated except for single, need to keep around for old transfers
export const DwollaLenderTransferType = z.enum(['principal', 'escrow', 'suspense', 'suspenseRemaining', 'single']);
export type DwollaLenderTransferType = z.infer<typeof DwollaLenderTransferType>;

export const DwollaTransferType = z.union([DwollaBorrowerTransferType, DwollaLenderTransferType]);
export type DwollaTransferType = z.infer<typeof DwollaTransferType>;

export const DwollaSweepType = z.enum(['sweepToBalance', 'sweepToAccount']);
export type DwollaSweepType = z.infer<typeof DwollaSweepType>;

export type MassTransfer = Partial<Record<'single', number>>;

export const DwollaBaseTransfer = z.object({
  id: zodBrandedUuid<TransferId>(),
  amount: z.number(),
  transferUrl: z.string(),
  status: PaymentStatus,
  paymentDate: zodJSInstantDate,
  type: DwollaTransferType,
});
export type DwollaBaseTransfer = z.infer<typeof DwollaBaseTransfer>;

// Dwolla transfer
export const Transfer = z.object({
  status: PaymentStatus,
  processedDate: zodJSInstantDate.optional(),
  toBalance: z.array(DwollaBaseTransfer),
  toAccount: z.array(DwollaBaseTransfer),
  clawbacks: z.array(DwollaBaseTransfer).default([]),
  failureCode: z.string().optional(),
});
export type Transfer = z.infer<typeof Transfer>;

/*
 * Usio Transfer
 */
export const UsioActiveTransfer = z.object({
  status: z.enum(['PENDING', 'PROCESSED', 'CANCELLED']),
  accountToken: z.string().optional(),
  batchedDate: zodJSInstantDate.optional(),
  batch: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
});
export const UsioFailedTransfer = z.object({
  status: z.literal('FAILED'),
  accountToken: z.string().optional(),
  batchedDate: zodJSInstantDate.optional(),
  batch: z.string().optional(),
  failureCode: z.string(),
  processedDate: zodJSInstantDate.optional(),
});
export type UsioFailedTransfer = z.infer<typeof UsioFailedTransfer>;
export type UsioActiveTransfer = z.infer<typeof UsioActiveTransfer>;
export const UsioTransfer = z.union([UsioActiveTransfer, UsioFailedTransfer]);
export type UsioTransfer = z.infer<typeof UsioTransfer>;

/*
 * Repay transfer
 */
export const RepayActiveTransfer = z.object({
  status: z.enum(['PENDING', 'PROCESSED', 'CANCELLED']),
  trackingNumber: z.string(),
  accountHolderId: z.string(),
  batch: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
  payAccountId: z.string().optional(), // Used to lock account if transfer fails for non R01 reasons
});
export type RepayActiveTransfer = z.infer<typeof RepayActiveTransfer>;
export const RepayFailedTransfer = z.object({
  status: z.literal('FAILED'),
  trackingNumber: z.string(),
  accountHolderId: z.string(),
  batch: z.string().optional(),
  payAccountId: z.string().optional(), // Used to lock account if transfer fails for non R01 reasons
  failureCode: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
});
export type RepayFailedTransfer = z.infer<typeof RepayFailedTransfer>;
export const RepayTransfer = z.union([RepayActiveTransfer, RepayFailedTransfer]);
export type RepayTransfer = z.infer<typeof RepayTransfer>;

export const BorrowerPaymentPayer = z.object({
  id: zodBrandedUuid<BorrowerId>(),
  type: z.literal('borrower'),
});
export type BorrowerPaymentPayer = z.infer<typeof BorrowerPaymentPayer>;

export const AuthorizedContactPaymentPayer = z.object({
  id: zodBrandedUuid<AuthorizedContactId>(),
  type: z.literal('authorizedContact'),
});
export type AuthorizedContactPaymentPayer = z.infer<typeof AuthorizedContactPaymentPayer>;

export const EntityPaymentPayer = z.object({
  id: zodBrandedUuid<EntityId>(),
  type: z.literal('entity'),
});
export type EntityPaymentPayer = z.infer<typeof EntityPaymentPayer>;

export const ThirdPartyPaymentPayer = z.object({
  name: z.string(),
  type: z.literal('thirdParty'),
});
export type ThirdPartyPaymentPayer = z.infer<typeof ThirdPartyPaymentPayer>;

export const PaymentPayer = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
  ThirdPartyPaymentPayer,
]);
export type PaymentPayer = z.infer<typeof PaymentPayer>;

const BalanceDebitSource = z.object({
  type: z.literal('balance'),
  balance: z.enum(['unappliedSuspense', 'futureStatementSuspense']),
  amount: z.number(),
});
export type BalanceDebitSource = z.infer<typeof BalanceDebitSource>;
const ExternalDebitSource = z.object({
  type: z.literal('external'),
  amount: z.number(),
});
export type ExternalDebitSource = z.infer<typeof ExternalDebitSource>;
export const PaymentDebitSource = z.discriminatedUnion('type', [BalanceDebitSource, ExternalDebitSource]);
export type PaymentDebitSource = z.infer<typeof PaymentDebitSource>;

export const DEFAULT_CREATED_DATE_PLZ_MIGRATE = toJSInstantDate(new Date('1972-12-14T22:54:37.000Z'));

const PaymentBase = z.object({
  id: zodBrandedUuid<PaymentId>(),
  loanId: zodBrandedUuid<LoanId>(),
  payer: PaymentPayer,
  total: z.number(),
  transactionId: z.string(),
  paymentDate: zodJSMixedDate,
  failureDate: zodJSInstantDate.optional(),
  cancelledDate: zodJSInstantDate.optional(),
  processedDate: zodJSInstantDate.optional(),
  createdDate: zodJSInstantDate.default(DEFAULT_CREATED_DATE_PLZ_MIGRATE), // LESLIE TODO: Make required
  status: PaymentStatus,
  holdback: z.number().optional(),
  additionalPrincipal: z.number().optional(),
  additionalEscrow: z.number().optional(),
  additionalSuspense: z.number().optional(),
  ignoreReserveCredit: z.boolean().optional(),
  debitSource: PaymentDebitSource.optional(),
  comment: z.string().optional(),
  escrowShortageAnalysisId: zodBrandedUuid<EscrowPaymentHistoryItemId>().optional(),
  paymentConfigId: zodBrandedUuid<PaymentConfigId>().optional(), // TODO: Deprecate this
  delinquentInterestSettingId: zodBrandedUuid<DelinquentInterestSettingId>().optional(),
  isDeposit: z.boolean().optional(),
  payoffId: zodBrandedUuid<PayoffId>().optional(),
});

export const NonAchPayment = PaymentBase.extend({
  method: z.enum([
    PaymentMethod.enum.check,
    PaymentMethod.enum.wire,
    PaymentMethod.enum.phone,
    PaymentMethod.enum.creditCard,
    PaymentMethod.enum.debitCard,
    PaymentMethod.enum.reversal,
    PaymentMethod.enum.reserveReversal,
    PaymentMethod.enum.balanceForward,
    PaymentMethod.enum.principalPrepayment,
    PaymentMethod.enum.balanceReallocation,
  ]),
});
export type NonAchPayment = z.infer<typeof NonAchPayment>;

export const ReservePayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.reserve),
  transfer: Transfer.optional(),
  sweep: Transfer.optional(),
  isAutopayment: z.boolean().optional(),
});
export type ReservePayment = z.infer<typeof ReservePayment>;

export const DwollaPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.dwolla),
  transfer: Transfer.optional(),
  sweep: Transfer.optional(),
  isAutopayment: z.boolean().optional(),
});
export type DwollaPayment = z.infer<typeof DwollaPayment>;

const SweepEnabledPayment = z.union([DwollaPayment, ReservePayment]);
export type SweepEnabledPayment = z.infer<typeof SweepEnabledPayment>;

export const UsioPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.authorizedACH),
  isAutopayment: z.boolean().optional(),
  transfer: UsioTransfer.optional(), // TODO: make required
  isSameDayACH: z.boolean().optional(),
});
export type UsioPayment = z.infer<typeof UsioPayment>;

export const RepayPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.repayACH),
  isAutopayment: z.boolean().optional(),
  transfer: RepayTransfer.optional(), // TODO: make required
});
export type RepayPayment = z.infer<typeof RepayPayment>;

export const Payment = z.union([NonAchPayment, SweepEnabledPayment, UsioPayment, RepayPayment]);
export type Payment = z.infer<typeof Payment>;

// DwollaBaseTransfer with added prisma fields
const PaymentTransfer = DwollaBaseTransfer.extend({
  paymentId: zodBrandedUuid<PaymentId>(),
  isSweep: z.boolean(),
});
export type PaymentTransfer = z.infer<typeof PaymentTransfer>;

export const PaymentAllowedStatuses = z.union([
  LoanStatusPreActive,
  LoanStatusActive,
  LoanStatusForeclosure,
  LoanStatusPendingTransfer,
  LoanStatusTransferred,
]);

export const isAchPayment = (payment: Payment): payment is DwollaPayment | UsioPayment | RepayPayment =>
  ['dwolla', 'authorizedACH', 'repayACH'].includes(payment.method);
export const isRepayPayment = (payment: Payment): payment is RepayPayment => payment.method === 'repayACH';
export const isDwollaPayment = (payment: Payment): payment is DwollaPayment => payment.method === 'dwolla';
export const isDepositPayment = (payment: Payment): boolean => payment.isDeposit === true;
export const isReversalPayment = (payment: Payment): boolean =>
  ['reversal', 'reserveReversal'].includes(payment.method);
